import ExportDropdown, {
  type ExportOption,
} from "@/components/design-system/export-dropdown";
import { Badge } from "@/components/design-system/badge";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CSVIcon,
  downloadFileFromBlob,
  errorToast,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  PDFIcon,
  ROLE_PERMISSIONS,
} from "@/utils/constants";
import {
  showApiErrorToast,
  showApiSuccessToast,
} from "../utils/apiToastMessage";
import {
  Eye,
  Globe,
  MoreVertical,
  Pencil,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { selectUserRole } from "@/store/slices/authSlice";
import { classApi } from "../api/classApi";
import { buildPublicClassPreviewUrl } from "../utils/publicClassPreviewUrl";
import { CLASSES_PER_PAGE } from "../constants/class.constants";
import type { AdminClass } from "../types/class.types";

export default function ClassListSection() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>();
  const [classes, setClasses] = useState<AdminClass[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [classToToggle, setClassToToggle] = useState<{
    id: string;
    is_active: boolean;
    label?: string;
  } | null>(null);
  const userRole = useSelector(selectUserRole);
  const userPerms = (userRole?.permissions as string[]) ?? [];

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await classApi.listClasses({
        page: currentPage,
        pageSize: CLASSES_PER_PAGE,
        search: searchValue,
        fromDate,
        toDate,
        sortBy: sortConfig?.key,
        sortOrder: sortConfig?.direction,
      });
      setClasses(result.data);
      setTotalItems(result.totalItems);
      setTotalPages(result.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchValue, fromDate, toDate, sortConfig]);

  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config ?? undefined);
    if (currentPage !== 1) setCurrentPage(1);
  };

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      const response = await classApi.exportClasses(type, {
        search: searchValue,
        fromDate,
        toDate,
      });
      downloadFileFromBlob(response, type, "class_report");
      showApiSuccessToast(
        response.message,
        `Classes exported as ${FileFormats[type].name} successfully`
      );
    } catch (e) {
      console.error(e);
      showApiErrorToast(e, "Could not export classes. Please try again.");
    }
  };

  const exportOptions: ExportOption[] = [
    { label: "PDF", icon: <PDFIcon />, action: () => handleExport("pdf") },
    { label: "Excel", icon: <ExcelIcon />, action: () => handleExport("xl") },
    { label: "CSV", icon: <CSVIcon />, action: () => handleExport("csv") },
  ];

  const handleToggleClassActive = async () => {
    if (!classToToggle) return;

    setToggleLoading(true);
    try {
      const result = await classApi.toggleClassActive(
        classToToggle.id,
        classToToggle.is_active
      );
      showApiSuccessToast(result.message, "Class status updated successfully");
      setActivateModalOpen(false);
      setClassToToggle(null);
      await fetchList();
    } catch (e) {
      console.error(e);
      showApiErrorToast(e, "Could not update class status. Please try again.");
    } finally {
      setToggleLoading(false);
    }
  };

  const openPreview = (record: AdminClass) => {
    const url = buildPublicClassPreviewUrl(record);
    if (!url) {
      errorToast(
        "Preview unavailable",
        "This class is not linked to the public catalog yet."
      );
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const columns: TableColumn<AdminClass>[] = [
    {
      key: "sno",
      title: "S.No",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => (
        <div className="text-center text-[16px] font-medium text-[#1F1F1F]">
          {String((currentPage - 1) * CLASSES_PER_PAGE + index + 1).padStart(2, "0")}
        </div>
      ),
    },
    {
      key: "name",
      title: "Class Name",
      minWidth: 280,
      sortable: true,
      sortKey: "name",
      render: (_, record) => (
        <div
          className="text-[16px] font-medium text-[#1F1F1F]"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "students",
      title: "No of Students Onboarded (Paid Students)",
      minWidth: 220,
      sortable: true,
      sortKey: "studentsOnboarded",
      render: (_, record) => (
        <div className="text-[16px] font-medium text-[#1F1F1F]">
          {record.studentsOnboarded}
        </div>
      ),
    },
    {
      key: "totalPrice",
      title: "Total Price",
      minWidth: 120,
      sortable: true,
      sortKey: "totalPrice",
      render: (_, record) => (
        <div className="text-[16px] font-medium text-[#1F1F1F]">
          {record.totalPrice.toLocaleString("en-IN")}
        </div>
      ),
    },
    {
      key: "validity",
      title: "Validity (days)",
      minWidth: 120,
      sortable: true,
      sortKey: "validityDays",
      render: (_, record) => (
        <div className="text-[16px] font-medium text-[#1F1F1F]">
          {record.validityDays}
        </div>
      ),
    },
    {
      key: "status",
      title: "Active/Inactive",
      minWidth: 178,
      maxWidth: 178,
      align: "center",
      sortable: true,
      sortKey: "isActive",
      render: (_, record) => (
        <Badge variant={record.isActive ? "success" : "danger"}>
          {record.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "action",
      title: "Action",
      minWidth: 180,
      maxWidth: 180,
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="p-1 hover:bg-gray-100 rounded-full"
            aria-label="View class"
            title="View class"
            onClick={() => navigate(`/class-management/view/${record.id}`)}
          >
            <Eye className="w-6 h-6 text-[#1C1B1F]" />
          </button>
          <button
            type="button"
            className="p-1 hover:bg-gray-100 rounded-full"
            aria-label="Edit class"
            title="Edit class"
            onClick={() => navigate(`/class-management/edit/${record.id}`)}
          >
            <Pencil className="w-6 h-6 text-[#1C1B1F]" />
          </button>
          <button
            type="button"
            className="p-1 hover:bg-gray-100 rounded-full"
            aria-label="Preview on site"
            title="Preview on site"
            onClick={() => openPreview(record)}
          >
            <Globe className="w-6 h-6 text-[#1C1B1F]" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="p-1 hover:bg-gray-100 rounded-full"
                aria-label="More actions"
              >
                <MoreVertical className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[180px] !p-0 rounded-[8px] border border-[#EEE] bg-[#FFF] shadow-[0_30px_40px_0_rgba(0,0,0,0.15)]"
            >
              <DropdownMenuItem
                className="h-[42px] px-4 py-0 flex cursor-pointer items-center justify-between gap-2 rounded-none hover:bg-transparent focus:bg-transparent [&_svg]:!size-6"
                onClick={() => {
                  setClassToToggle({
                    id: record.id,
                    is_active: !record.isActive,
                    label: record.name,
                  });
                  setActivateModalOpen(true);
                }}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  {record.isActive ? (
                    <>
                      <span>Deactivate</span>
                      <ToggleLeft className="h-6 w-6 text-[#FF152C]" />
                    </>
                  ) : (
                    <>
                      <span>Activate</span>
                      <ToggleRight className="h-6 w-6 text-[#00C41D]" />
                    </>
                  )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title="Class List"
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          if (currentPage !== 1) setCurrentPage(1);
        }}
        onDateRangeChange={(start, end) => {
          setFromDate(start);
          setToDate(end);
          if (currentPage !== 1) setCurrentPage(1);
        }}
        rightAction={
          <>
            {(hasMenuPermission(ROLE_PERMISSIONS.BUTTON_EXPORT_CLASS, userPerms) ||
              hasMenuPermission(ROLE_PERMISSIONS.BUTTON_EXPORT_COURSE, userPerms)) && (
              <ExportDropdown options={exportOptions} />
            )}
            <Button
              type="button"
              variant="primary"
              size="md"
              className="h-[48px] w-[161px] px-[12px] py-[13px] gap-[4px] rounded-[12px] bg-[#5C88C5] text-white hover:bg-[#5C88C5] !text-[18px] !font-semibold !leading-normal"
              onClick={() => navigate("/class-management/create")}
            >
              Add Class
            </Button>
          </>
        }
      />

      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <Table
          columns={columns}
          data={classes}
          rowKey="id"
          sticky
          scroll={{ x: 1278 }}
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={CLASSES_PER_PAGE}
        itemsLabel="Classes"
      />

      <Modal
        isOpen={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        title={classToToggle?.is_active ? "Activate" : "Deactivate"}
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="mb-8 text-[24px] text-[#7B7B7B]">
            {classToToggle?.is_active ? (
              <>
                Are you sure you want to activate{" "}
                <span className="font-semibold text-[#1F1F1F]">
                  {classToToggle.label || "this class"}
                </span>
                ?
              </>
            ) : (
              <>
                Are you sure you want to deactivate{" "}
                <span className="font-semibold text-[#1F1F1F]">
                  {classToToggle?.label || "this class"}
                </span>
                ?
                <br />
                Once deactivated, this class will no longer be available to
                students and teachers.
              </>
            )}
          </p>
        </div>
        <div className="flex justify-end gap-4 border-t border-gray-200 p-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="h-[48px] w-[120px] border border-[#5C88C5] bg-[#fff] text-dark"
            onClick={() => setActivateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className="h-[48px] w-[142px] bg-[#5C88C5] text-white"
            onClick={() => handleToggleClassActive()}
            disabled={toggleLoading}
          >
            {toggleLoading
              ? "Updating..."
              : classToToggle?.is_active
                ? "Yes, Activate"
                : "Yes, Deactivate"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
